import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { FreeMode, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ChevronRightIcon from 'utils/icon/icon-chevron-right.svg';
import clsx from 'clsx';
import useResponsive from 'utils/responsive';
import { isLauraCanada } from 'utils/helpers';
import * as queries from 'utils/storefront/queries';
import Storefront from 'utils/storefront';
import ShopTheLookCard from './shop-the-look-card';

const ShopTheLook = ({ 
  id, title, blocks, products,
  useProducts, templateName }) => {
  const storefront = Storefront.getInstance();
  const [cards, setCards] = useState(
    document.body.classList.contains('template-product') && useProducts
      ? products.length
        ? products
        : []
      : blocks,
  );
  const { breakPoint } = useResponsive();
  let cardLength = 3;
  if (templateName === 'product') {
    cardLength = 1;
  }

  const isSlider = useMemo(() => {
    if (breakPoint === 'md') {
      return cards.length > 3;
    }
    if (breakPoint === 'tablet') {
      return cards.length > 4;
    }
    if (breakPoint === 'lg') {
      return cards.length > 5;
    }
    return cards.length > cardLength;
  }, [breakPoint, cards]);

  useEffect(() => {
    const productUpdateHandle = async (e) => {
      if (useProducts) {
        if (e.detail.shopTheLook?.value) {
          const ids = JSON.parse(e.detail.shopTheLook.value);
          const response = await storefront.request({
            query: queries.getProductsByIds,
            variables: {
              ids,
            },
          });

          if (!response.data) {
            // eslint-disable-next-line no-console
            console.error(
              'shop-the-look/shop-the-look.js: Storefront API request failed',
              response,
            );
            setCards([]);
          }
          setCards(
            response.data.nodes.map((item) => ({
              id: item.id,
              title: item.title,
              text: '',
              product: {
                id: item.id,
                handle: item.handle,
                type: item.productType,
              },
              btnText: '',
              btnLink: '',
              image: item.images?.edges?.[0]?.node || null,
            })),
          );
        } else {
          setCards([]);
        }
      }
    };
    document.addEventListener('productUpdated', productUpdateHandle);
    return () => {
      document.removeEventListener('productUpdated', productUpdateHandle);
    };
  }, []);

  if (!cards.length) {
    return null;
  }

  return (
    <div className={`
    lc:bg-accent-3 ml:bg-accent-1
    shop-the-look-main-${templateName}`}>
      <div className="container">
        <div className="col-span-full py-40 lg:py-56">
          {!!title && (
            <h2 className="display-4 text-center md:px-80 lc:text-left lc:md:text-center mb-24 normal-case ml:md:mb-40">
              {title}
            </h2>
          )}
          {isSlider ? (
            <div className="relative">
              <div
                className={clsx(
                  'hidden md:flex absolute bottom-full right-0 lc:pt-2',
                  {
                    'mb-24 ml:mb-36': !!title,
                    'mb-11 ml:mb-2': !title,
                  },
                )}
              >
                <button className="prev w-32 h-32 rounded-full lc:border border-grey-900 flex justify-center items-center mx-4 lc:mx-8 pr-2">
                  <ChevronRightIcon className="w-16 ml:w-24 h-auto rotate-180" />
                </button>
                <button className="next w-32 h-32 rounded-full lc:border border-grey-900 flex justify-center items-center pl-2">
                  <ChevronRightIcon className="w-16 ml:w-24 h-auto" />
                </button>
              </div>
              <Swiper
                className="!-mx-20 !px-24 md:!mx-0 md:!px-0"
                slidesPerView={'auto'}
                freeMode={true}
                spaceBetween={8}
                modules={[Navigation, FreeMode]}
                navigation={{
                  prevEl: `#${id} .prev`,
                  nextEl: `#${id} .next`,
                  disabledClass: 'text-disabled !border-grey-400',
                }}
                breakpoints={{
                  0: {
                    slidesPerView: 1.25,
                    spaceBetween: isLauraCanada() ? 8 : 4,
                  },
                  768: {
                    spaceBetween: isLauraCanada() ? 8 : 4,
                  },
                  1200: {
                    spaceBetween: isLauraCanada() ? 24 : 4,
                  },
                }}
              >
                {cards.map((card) => (
                  <SwiperSlide
                    key={card.id}
                    className={clsx('w-full', {
                      'md:!w-[calc((100%_-_48px)/3)] tablet:!w-[calc((100%_-_72px)/4)] lg:!w-[calc((100%_-_96px)/5)]':
                        isLauraCanada(),
                      'md:!w-[calc((100%_-_8px)/3)]  tablet:!w-[calc((100%_-_24px)/4)] lg:!w-[calc((100%_-_32px)/5)]':
                        !isLauraCanada(),
                    })}
                  >
                    <ShopTheLookCard {...card} total={cards.length} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className="flex flex-col gap-24 ml:gap-48 ml:md:gap-4 md:flex-row lc:md:gap-8 lc:tablet:gap-24 justify-center">
              {cards.map((card) => (
                <div
                  className="flex-1 lc:max-w-[428px] md:w-0 ml:max-w-440"
                  key={card.id}
                >
                  <ShopTheLookCard {...card} total={cards.length} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    
  );
};

export default ShopTheLook;
